import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Safe storage: when Tracking Prevention (or similar) blocks sessionStorage, use in-memory fallback
// so auth doesn't throw and the Navigator Lock doesn't hang.
const memoryFallback = {};
const safeSessionStorage = {
  getItem: (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return memoryFallback[key] ?? null;
    }
  },
  setItem: (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      memoryFallback[key] = value;
    }
  },
  removeItem: (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch { /* ignore */ }
    delete memoryFallback[key];
  }
};

// Clear any stale auth data from localStorage on startup
try {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
} catch (e) {
  // silent — localStorage may not be available
}

// No-op lock: avoids Navigator LockManager timeout when browser blocks storage (e.g. Tracking Prevention).
// Cross-tab sync is skipped in that case; session still works in the current tab.
const noOpLock = (name, acquireTimeout, fn) => fn();

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    storage: safeSessionStorage,
    detectSessionInUrl: false,
    lock: noOpLock,
    lockAcquireTimeout: 0
  }
})

// Session heartbeat — proactively refresh token every 10 minutes to prevent expiry
let _heartbeatInterval = null;
export const startSessionHeartbeat = () => {
  if (_heartbeatInterval) return; // already running
  _heartbeatInterval = setInterval(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const expiresAt = session.expires_at * 1000;
        const timeLeft = expiresAt - Date.now();
        // Refresh if less than 15 minutes remaining
        if (timeLeft < 15 * 60 * 1000) {
          await supabase.auth.refreshSession();
        }
      }
    } catch (e) {
      // silent — don't crash the app
    }
  }, 10 * 60 * 1000); // every 10 minutes
};
export const stopSessionHeartbeat = () => {
  if (_heartbeatInterval) {
    clearInterval(_heartbeatInterval);
    _heartbeatInterval = null;
  }
};

// Session management
export const sessionManager = {
  ensureValidSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session) return false;

      const expiresAt = session.expires_at * 1000;
      const timeLeft = expiresAt - Date.now();

      if (timeLeft < 10 * 60 * 1000) {
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          // Retry once
          await new Promise(r => setTimeout(r, 1000));
          const { error: retryError } = await supabase.auth.refreshSession();
          return !retryError;
        }
      }
      return true;
    } catch {
      return false;
    }
  },

  withValidSession: async (callback) => {
    const isValid = await sessionManager.ensureValidSession();
    if (!isValid) {
      throw new Error('Session invalid or expired. Please sign in again.');
    }
    return await callback();
  }
};

// Auth helper functions
export const auth = {
  // Sign up new user
  signUp: async (email, password, userData = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    })
    return { data, error }
  },

  // Sign in user
  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  // Sign out user
  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  // Get current user
  getCurrentUser: () => {
    return supabase.auth.getUser()
  },

  // Listen to auth changes
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Cached admin status — avoid calling check_admin_status RPC multiple times per session
let _adminStatusCache = { email: null, result: null }
export async function getAdminStatusCached(userEmail) {
  if (!userEmail) return { data: null, error: null }
  if (_adminStatusCache.email === userEmail) return _adminStatusCache.result
  const { data, error } = await supabase.rpc('check_admin_status', { user_email: userEmail })
  _adminStatusCache = { email: userEmail, result: { data, error } }
  return { data, error }
}
export function clearAdminStatusCache() {
  _adminStatusCache = { email: null, result: null }
}

// Ensure the current auth user has a row in public.users (so bookings.user_id FK works).
// Call after sign-in/sign-up; safe to call every time (upserts by id).
export async function ensurePublicUser(user) {
  if (!user?.id) return { success: false }
  const fullName = user.user_metadata?.full_name || user.user_metadata?.name || 'User'
  const phone = user.user_metadata?.phone || null
  const { data, error } = await supabase.rpc('ensure_public_user', {
    p_user_id: user.id,
    p_email: user.email ?? '',
    p_full_name: fullName,
    p_phone: phone
  })
  if (error) return { success: false, error }
  return data || { success: false }
}

// Mail is configured once for all Edge Functions — see supabase/functions/_shared/mail.ts
// (SMTP_* + MAIL_FROM, or RESEND_API_KEY + MAIL_FROM, plus ADMIN_EMAIL for admin alerts).

// Notify admin when a customer creates a pending booking (one email per request).
export async function notifyPendingBooking(payload) {
  try {
    const { data, error } = await supabase.functions.invoke('notify-pending-booking', {
      body: {
        customer_name: payload.customer_name,
        customer_phone: payload.customer_phone,
        customer_email: payload.customer_email ?? '',
        booking_date: payload.booking_date,
        time_slots: payload.time_slots,
        total_amount: payload.total_amount,
      },
    });
    if (error) console.warn('Notify pending booking:', error);
    return data;
  } catch (e) {
    console.warn('Notify pending booking:', e);
    return null;
  }
}

// Notify customer after admin approves (skipped server-side if no customer_email). Admin session required.
export async function notifyBookingApproved(payload) {
  try {
    const { data, error } = await supabase.functions.invoke('notify-booking-approved', {
      body: { booking_id: payload.booking_id },
    });
    if (error) {
      // FunctionsFetchError: fetch() never completed (network, CORS, wrong URL, ad blocker, local functions not running).
      // FunctionsHttpError: server responded 4xx/5xx — check Edge Function logs in Supabase Dashboard.
      const ctx = error.context
      console.warn('Notify booking approved:', error.message, ctx != null ? ctx : '');
    }
    return data;
  } catch (e) {
    console.warn('Notify booking approved:', e);
    return null;
  }
}

// Database helper functions
export const database = {
  // Get court info (single court)
  getCourt: async () => {
    const { data, error } = await supabase
      .from('courts')
      .select('*')
      .single()
    return { data, error }
  },

  _cache: { court: null, courtTime: 0, slots: null, slotsTime: 0 },

  getAvailability: async (date, courtId = null) => {
    try {
      const now = Date.now();
      const CACHE_TTL = 60000; // 1 minute cache for court & slot config

      const normalizeTime = (t) => {
        if (!t) return '';
        const str = String(t);
        const match = str.match(/^(\d{1,2}):(\d{2})/);
        if (match) return match[1].padStart(2, '0') + ':' + match[2];
        return str.substring(0, 5);
      };

      // STEP 1: Get court info (cached)
      let court = database._cache.court;
      if (!court || now - database._cache.courtTime > CACHE_TTL) {
        const { data, error: courtError } = await supabase
          .from('courts')
          .select('*')
          .single();
        if (courtError || !data) {
          throw new Error(`No court found: ${courtError?.message}`);
        }
        court = data;
        database._cache.court = court;
        database._cache.courtTime = now;
      }

      const targetCourtId = courtId || court.id;

      // STEP 2: Get slot config (cached)
      let activeSlots = database._cache.slots;
      if (!activeSlots || now - database._cache.slotsTime > CACHE_TTL) {
        const { data: configSlots, error: configError } = await supabase
          .from('time_slots')
          .select('slot_time, slot_type, is_active')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });
        if (configError) {
          console.error('Time slots config error:', configError);
        }
        activeSlots = configSlots || [];
        database._cache.slots = activeSlots;
        database._cache.slotsTime = now;
      }

      // STEP 3: Get booked slots (always fresh)
      const { data: bookedBookings } = await supabase
        .from('bookings')
        .select('slot_time')
        .eq('court_id', targetCourtId)
        .eq('booking_date', date)
        .in('status', ['confirmed', 'approved']);

      const bookedTimes = new Set();
      if (bookedBookings) {
        bookedBookings.forEach(booking => {
          const shortTime = normalizeTime(booking.slot_time);
          if (shortTime) bookedTimes.add(shortTime);
        });
      }

      const slotDate = new Date(date);
      const isWeekend = slotDate.getDay() === 0 || slotDate.getDay() === 6;

      const availabilityData = activeSlots.map(row => {
        const slotTimeNorm = normalizeTime(row.slot_time);
        const isAvailable = !bookedTimes.has(slotTimeNorm);
        const memberOnly = row.slot_type === 'members_only';
        const slotType = row.slot_type === 'coaching_hour' ? 'regular' : (memberOnly ? 'member_exclusive' : 'regular');
        return {
          court_id: targetCourtId,
          court_name: court.name,
          slot_date: date,
          slot_time: slotTimeNorm.includes(':') && slotTimeNorm.length === 5 ? slotTimeNorm + ':00' : slotTimeNorm,
          is_available: isAvailable,
          slot_type: slotType,
          member_only: memberOnly,
          is_weekend: isWeekend,
          slot_type_label: row.slot_type
        };
      });

      return { data: availabilityData, error: null };

    } catch (error) {
      console.error('Availability error:', error);
      return { data: null, error };
    }
  },

  // Get booked slots for a specific date (backup method)
  getBookedSlots: async (date, courtId = null) => {
    let query = supabase
      .from('booked_slots')
      .select('slot_time')
      .eq('booking_date', date)
    
    if (courtId) {
      query = query.eq('court_id', courtId)
    }
    
    const { data, error } = await query
    return { data, error }
  },

  // Get user's bookings
  getUserBookings: async (userId) => {
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        courts (name, description)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  // Check if user has active membership
  checkMembership: async (userId) => {
    const { data, error } = await supabase
      .rpc('has_active_membership', { user_uuid: userId })
    return { data, error }
  },

  // Get membership status
  getMembershipStatus: async (userId) => {
    const { data, error } = await supabase
      .rpc('get_membership_status', { user_uuid: userId })
    return { data, error }
  },

  // Create membership
  createMembership: async (membershipData) => {
    const { data, error } = await supabase
      .from('memberships')
      .insert([membershipData])
      .select()
      .single()
    return { data, error }
  },

  // Get user profile
  getUserProfile: async (userId) => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single()
    return { data, error }
  },

  // Update user profile
  updateUserProfile: async (userId, updates) => {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()
    return { data, error }
  },

  // Create user profile (after signup)
  createUserProfile: async (userData) => {
    const { data, error } = await supabase
      .from('users')
      .insert([userData])
      .select()
      .single()
    return { data, error }
  },

  // Get all bookings for admin (with optional date filter)
  getAllBookings: async (date = null) => {
    let query = supabase
      .from('bookings')
      .select(`
        *,
        users:user_id(full_name, email),
        courts:court_id(name)
      `)
      .order('booking_date', { ascending: false })
      .order('created_at', { ascending: false })

    if (date) {
      query = query.eq('booking_date', date)
    }

    const { data, error } = await query
    return { data, error }
  }
}

// SIMPLIFIED Real-time subscriptions - FOCUS ON CORE FUNCTIONALITY
export const realtime = {
  // Single subscription method for booked_slots - this is what we actually need
  subscribeToBookedSlots: (callback) => {
    const channelName = `booked_slots_${Math.random().toString(36).substring(7)}`;
    const channel = supabase.channel(channelName);
    
    channel
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'booked_slots' 
        }, 
        (payload) => {
          callback(payload);
        }
      )
      .subscribe((status, err) => {
        if (err) {
          console.error('Subscription error:', err);
        }
      });
    
    return channel;
  },

  startPollingAvailability: (callback, dateString, courtId, intervalMs = 8000) => {
    const pollData = async () => {
      try {
        const { data, error } = await database.getAvailability(dateString, courtId);
        if (!error && data) {
          callback({ type: 'poll', data });
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };
    
    pollData();
    const intervalId = setInterval(pollData, intervalMs);
    
    return {
      unsubscribe: () => {
        clearInterval(intervalId);
      }
    };
  }
}

export default supabase